import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, message, Typography, Space, Row, Col } from 'antd';
import { SafetyCertificateOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;

const OtpInput = ({
  email,
  type = 'login',
  onSuccess,
  onCancel,
  onBack,
  expiresIn = 300,
  // Endpoint & bentuk payload bisa diganti supaya komponen ini dipakai ulang
  // oleh login internal (admin), bukan hanya mitra. Nilai default = perilaku
  // lama, jadi pemanggil yang sudah ada tidak perlu diubah.
  verifyEndpoint,
  resendEndpoint = '/api/merchant/auth/resend-otp',
  identifierField = 'email',
  identifier
}) => {
  // Nilai yang dikirim ke server; untuk admin ini username, untuk mitra emailnya.
  const idValue = identifier !== undefined ? identifier : email;
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(expiresIn);
  const [isExpired, setIsExpired] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    // Allow only numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    const newOtpValues = [...otpValues];
    newOtpValues[index] = numericValue;
    setOtpValues(newOtpValues);

    // Auto focus next input
    if (numericValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle enter key
    if (e.key === 'Enter' && otpValues.filter(v => v).length === 6) {
      handleVerifyOtp();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePaste(e);
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData('text');
    const numericData = pastedData.replace(/[^0-9]/g, '').slice(0, 6);

    if (numericData.length === 6) {
      const newOtpValues = numericData.split('');
      setOtpValues(newOtpValues);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otpValues.join('');

    if (otpCode.length !== 6) {
      message.error('Mohon masukkan 6 digit kode OTP');
      return;
    }

    if (isExpired) {
      message.error('Kode OTP telah kedaluwarsa. Silakan minta kode baru.');
      return;
    }

    try {
      setLoading(true);

      const endpoint = verifyEndpoint || (type === 'registration'
        ? '/api/merchant/auth/verify-email'
        : '/api/merchant/auth/login-verify');

      const response = await axios.post(endpoint, {
        [identifierField]: idValue,
        otp_code: otpCode
      });

      message.success('OTP berhasil diverifikasi!');
      onSuccess(response.data);

    } catch (error) {
      console.error('OTP verification error:', error);
      const errorMessage = error.response?.data?.message || 'OTP tidak valid. Silakan coba lagi.';
      const remainingAttempts = error.response?.data?.remaining_attempts;

      if (remainingAttempts !== undefined) {
        message.error(`${errorMessage} (${remainingAttempts} percobaan tersisa)`);
      } else {
        message.error(errorMessage);
      }

      // Clear OTP inputs on error
      setOtpValues(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setResendLoading(true);

      const response = await axios.post(resendEndpoint, {
        [identifierField]: idValue,
        type
      });

      message.success('OTP baru telah dikirim ke email Anda');

      // Reset timer
      setTimeLeft(response.data.otp_expires_in || 300);
      setIsExpired(false);

      // Clear OTP inputs
      setOtpValues(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

    } catch (error) {
      console.error('Resend OTP error:', error);
      const errorMessage = error.response?.data?.message || 'Gagal mengirim OTP baru. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const isSubmitDisabled = otpValues.some(value => value === '') || isExpired || loading;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <div className="w-full max-w-md">
        <div
          className="shadow-2xl border-0 p-8 rounded-2xl"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
              <SafetyCertificateOutlined style={{ fontSize: '32px', color: 'white' }} />
            </div>
            <Title level={2} style={{ margin: 0, color: '#059669' }}>
              Verifikasi OTP
            </Title>
            <Text type="secondary" className="block mt-2">
              {type === 'registration'
                ? 'Masukkan kode OTP yang dikirim ke email Anda untuk verifikasi pendaftaran'
                : 'Masukkan kode OTP yang dikirim ke email Anda untuk melanjutkan login'
              }
            </Text>
            <Text strong className="block mt-2" style={{ color: '#1890ff' }}>
              {email}
            </Text>
          </div>

          <div className="mb-6">
            <Row gutter={8}>
              {otpValues.map((value, index) => (
                <Col span={4} key={index}>
                  <Input
                    ref={(el) => (inputRefs.current[index] = el)}
                    value={value}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    maxLength={1}
                    className="text-center"
                    style={{
                      height: '50px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      borderColor: isExpired ? '#ff4d4f' : '#d9d9d9',
                      backgroundColor: isExpired ? '#fff2f0' : '#ffffff'
                    }}
                    disabled={loading || isExpired}
                  />
                </Col>
              ))}
            </Row>
          </div>

          <div className="text-center mb-6">
            <Space direction="vertical" size="small">
              <Text type={isExpired ? 'danger' : 'secondary'}>
                <ClockCircleOutlined />
                {isExpired ? ' OTP Kedaluwarsa' : ` Waktu tersisa: ${formatTime(timeLeft)}`}
              </Text>

              <Button
                type="link"
                icon={<ReloadOutlined />}
                onClick={handleResendOtp}
                loading={resendLoading}
                disabled={resendLoading || !isExpired && timeLeft > 60}
                size="small"
              >
                Kirim Ulang OTP
              </Button>
            </Space>
          </div>

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button
              type="primary"
              block
              onClick={handleVerifyOtp}
              loading={loading}
              disabled={isSubmitDisabled}
              style={{
                height: '48px',
                borderRadius: '10px',
                background: isSubmitDisabled
                  ? '#d9d9d9'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {loading ? 'Memverifikasi...' : 'Verifikasi OTP'}
            </Button>

            <Space>
              {onBack && (
                <Button
                  type="default"
                  onClick={onBack}
                  style={{ borderRadius: '8px' }}
                >
                  Kembali
                </Button>
              )}
              {onCancel && (
                <Button
                  type="text"
                  onClick={onCancel}
                  style={{ borderRadius: '8px' }}
                >
                  Batal
                </Button>
              )}
            </Space>
          </Space>

          <div className="mt-6 text-center">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Tidak menerima email? Periksa folder spam atau
              <Button type="link" size="small" onClick={handleResendOtp} loading={resendLoading}>
                kirim ulang OTP
              </Button>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpInput;